import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { referralsAPI, type CreatorReferralSummary } from '../../services/api'
import { apiErrorMessage } from '../../utils/apiError'
import ConfirmDialog from '../ConfirmDialog'

/**
 * Monetization → Subscribers: whether subscribers who bring in a new paying
 * subscriber get a free month, and what referrals have brought in. Off by
 * default: the month comes out of the creator's revenue.
 */
const ReferralSettingsCard = () => {
  const [summary, setSummary] = useState<CreatorReferralSummary | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let live = true
    referralsAPI
      .settings()
      .then((s) => live && setSummary(s))
      .catch(() => live && setSummary(null))
    return () => {
      live = false
    }
  }, [])

  if (!summary) return null

  const set = async (enabled: boolean) => {
    setBusy(true)
    try {
      setSummary(await referralsAPI.setRewards(enabled))
      toast.success(enabled ? 'Referral months on' : 'Referral months off')
      setConfirming(false)
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't save the setting"))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4" aria-labelledby="referral-settings-heading" data-testid="referral-settings">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 id="referral-settings-heading" className="font-semibold text-gray-900">
            Referrals
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            Subscribers get a share link on your page.{' '}
            {summary.rewards_enabled
              ? 'Each new subscriber it brings in earns the subscriber who shared it a free month, once the new subscriber has paid (up to 12 a year each).'
              : 'Turn on free months to reward subscribers who bring in new paying subscribers.'}
          </p>
          <p className="mt-1 text-xs text-gray-500" data-testid="referral-summary">
            {summary.subscribers_referred} {summary.subscribers_referred === 1 ? 'subscriber' : 'subscribers'} came
            through referrals · {summary.months_given} free {summary.months_given === 1 ? 'month' : 'months'} given
          </p>
        </div>
        <button
          type="button"
          onClick={() => (summary.rewards_enabled ? set(false) : setConfirming(true))}
          disabled={busy}
          className="shrink-0 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {summary.rewards_enabled ? 'Turn off free months' : 'Turn on free months'}
        </button>
      </div>
      <ConfirmDialog
        isOpen={confirming}
        title="Give a free month for each referral?"
        message="A subscriber whose link brings in a new paying subscriber gets their next month with you free, so you don't earn from them that month. At most 12 months a year per subscriber. You can turn it off any time; months already earned stay."
        confirmLabel="Turn on"
        confirming={busy}
        onConfirm={() => set(true)}
        onCancel={() => setConfirming(false)}
      />
    </section>
  )
}

export default ReferralSettingsCard
